import { Fragment, FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { classNames } from "primereact/utils";

import { faCircleDashed } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { applicationConfig } from "../../../../../environmentConfiguration";
import { translationKeys } from "../../../../models/app-translation-keys";
import { RouteConstants } from "../../../../models/enums";
import { IMenuItem } from "../../../../models/interfaces";
import { useStore } from "../../../../stores/StoreContext";

import "./FeatureSelection.scss";

const FeatureSelectionRaw: FunctionComponent = () => {
  const { t } = useTranslation();
  const { userStore, navigationStore, permissionStore } = useStore();
  const aiFeatureMenuRef = useRef<Menu>(null);

  const [filteredFeatureMenuItems, setFilteredFeatureMenuItems] = useState<IMenuItem[]>([]);
  const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);

  const navigate = useNavigate();

  const navigateToPath = (event: any, path: string) => {
    navigate(path.startsWith("/") ? path : `/${path}`);
    aiFeatureMenuRef.current?.hide(event);
  };

  const navigateToExternalUrl = (event: any, url: string) => {
    window.open(url, "_blank");
  };

  const navigateTo = (event: any, item: IMenuItem) => {
    if (item.externalUrl) {
      navigateToExternalUrl(event, item.externalUrl);
    } else if (item.url) {
      navigateToPath(event, item.url ?? "");
    }
    aiFeatureMenuRef.current?.hide(event);
  };

  const menuItemTemplate = (item: any) => (
    <Link
      className="menu-link p-link"
      onClick={event => {
        if (item.externalUrl) {
          event.preventDefault();
          navigateTo(event, item);
        }
      }}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          navigateTo(event, item);
        }
      }}
      tabIndex={0}
      role="link"
      to={item.externalUrl ?? item.url ?? "#"}
      target={item.externalUrl ? "_blank" : undefined}
      rel={item.externalUrl ? "noopener noreferrer" : undefined}
    >
      <div className="menu-link-icon">{item.icon()}</div>

      {t(item?.label ?? "")}
    </Link>
  );

  useEffect(() => {
    const menuItems: IMenuItem[] = filteredFeatureMenuItems.map(item => {
      return {
        ...item,
        template: menuItemTemplate,
      };
    });
    setMenuItems(menuItems);
  }, [filteredFeatureMenuItems]);

  useEffect(() => {
    setFilteredFeatureMenuItems(navigationStore.getPermissionedFeatureMenuItems(permissionStore.userPermissions.roles));
  }, [permissionStore.userPermissions]);

  if (
    navigationStore.currentActiveMenuItem?.path === RouteConstants.LandingPage ||
    (applicationConfig.featureFlags.enableTermsOfUse && !userStore.userAcceptedTermsOfUseAndPrivacyPolicy)
  ) {
    return null;
  }

  return (
    <div className="ai-feature-container">
      {menuItems.length > 0 ? (
        <Fragment>
          <Menu model={menuItems} popup={true} ref={aiFeatureMenuRef} id="popup_menu_left" popupAlignment="left" />

          <Button
            label={t(translationKeys.feature.selectAiFeature)}
            className={classNames("icon-button-reverse", { "is-focused": false })}
            onClick={event => aiFeatureMenuRef.current?.toggle(event)}
            aria-controls="popup_menu_left"
            aria-haspopup
            icon={<FontAwesomeIcon icon={faCircleDashed} />}
            text
          />
        </Fragment>
      ) : (
        <span>{t(translationKeys.feature.noAiToolsAvailable)}</span>
      )}
    </div>
  );
};

export const FeatureSelection = observer(FeatureSelectionRaw);
